/**
 * FM "Export to Excel" (F1) — self-dependent port of `fmdashboard.controller.js` `export()`.
 * Flattens the team calendar into the same 17-column layout the GRM `exportToExcelService`
 * produces, then builds a CSV and triggers a client-side download. FXP hosts can override with
 * the real xlsx service by passing `onExport` to the grid; this keeps local dev/CT self-dependent.
 */

/** Column headers — exact parity of `excelColumnHeaders` in `export()`. */
export const EXPORT_COLUMN_HEADERS: string[] = [
  'Resource Name',
  'Proj Name/Event Name',
  'Request Timezone',
  'Request ID',
  'Start Datetime',
  'End Datetime',
  'Customer Name ',
  'Delivery Location',
  'Demand Source ID',
  'Assignment Status',
  'Offering /Product',
  'RequestorAlias',
  'Comments',
  'Travel Notes',
  'Request Type',
  'Delivery Type ',
  'Requested Hours',
];

/** Per-event additional properties (parity of `calendarObj.AdditionalProperties`). */
export interface ExportEventAdditionalProperties {
  IsSystemGenerated?: string | boolean;
  CustomerName?: string;
  DeliveryLocation?: string;
  /** The GRM wire sends this as a string OR a number depending on the demand source. */
  DemandSourceId?: string | number;
  MIP?: string;
  RequestorAlias?: string;
  Comments?: string;
  TravelNotes?: string;
  DeliveryType?: string;
}

/** A single calendar item to export (parity of `calendarObj`). */
export interface ExportCalendarItem {
  Id?: string | number;
  Name?: string;
  TimeZone?: string;
  StartDate?: string;
  EndDate?: string;
  Status?: string;
  AssignmentType?: string;
  TotalDuration?: string | number;
  AdditionalProperties?: ExportEventAdditionalProperties;
}

/** A resource with its calendar items (parity of the `teamCalendarData` rows). */
export interface ExportResource {
  FullName?: string;
  CalendarItems?: ExportCalendarItem[];
}

const NA = 'NA';
const isTrue = (v?: string | boolean): boolean =>
  typeof v === 'boolean' ? v : String(v ?? '').toLowerCase() === 'true';

/** Flatten team calendar data into export rows — parity of the nested `angular.forEach` in `export()`. */
export function buildForecastExportRows(resources: ExportResource[]): string[][] {
  const rows: string[][] = [];
  for (const obj of resources || []) {
    for (const item of obj.CalendarItems || []) {
      const ap = item.AdditionalProperties || {};
      const isProjectEvent = isTrue(ap.IsSystemGenerated);
      const status = isProjectEvent
        ? item.Status ?? ''
        : (item.Status ?? '').toLowerCase() === 'confirmed'
        ? 'Approved'
        : 'Pending';
      rows.push([
        obj.FullName ?? '',
        item.Name ?? '',
        item.TimeZone ?? '',
        isProjectEvent ? String(item.Id ?? '') : NA,
        item.StartDate ?? '',
        item.EndDate ?? '',
        isProjectEvent ? ap.CustomerName ?? '' : NA,
        isProjectEvent ? ap.DeliveryLocation ?? '' : NA,
        isProjectEvent ? String(ap.DemandSourceId ?? '') : NA,
        status,
        isProjectEvent ? ap.MIP ?? '' : NA,
        isProjectEvent ? ap.RequestorAlias ?? '' : NA,
        ap.Comments ?? '',
        isProjectEvent ? ap.TravelNotes ?? '' : NA,
        isProjectEvent ? item.AssignmentType ?? '' : NA,
        isProjectEvent ? ap.DeliveryType ?? '' : NA,
        isProjectEvent ? String(item.TotalDuration ?? '') : NA,
      ]);
    }
  }
  return rows;
}

const neutralizeSpreadsheetFormula = (value: string): string =>
  /^[\t\r\n ]*[=+\-@]/.test(value) ? `'${value}` : value;

const escapeCsv = (value: string): string => {
  const safeValue = neutralizeSpreadsheetFormula(value);
  return /[",\n\r]/.test(safeValue)
    ? `"${safeValue.replace(/"/g, '""')}"`
    : safeValue;
};

/** Build a CSV string (header + rows). */
export function toCsv(headers: string[], rows: string[][]): string {
  return [headers, ...rows].map((r) => r.map((c) => escapeCsv(String(c ?? ''))).join(',')).join('\r\n');
}

/** `Resource_Event_details_<MMM-YYYY>` base file name (parity of `filemName`). */
export function exportFileName(monthDate: Date, ext = 'csv'): string {
  const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
    monthDate.getMonth()
  ];
  return `Resource_Event_details_${m}-${monthDate.getFullYear()}.${ext}`;
}

/** Trigger a browser download of `csv` (no-op outside a DOM, e.g. jsdom without URL.createObjectURL). */
export function downloadCsv(csv: string, fileName: string): void {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) {
    return;
  }
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Build + download the forecast export in one call. Returns the row count exported (0 => "No records"). */
export function exportForecastToCsv(resources: ExportResource[], monthDate: Date): number {
  const rows = buildForecastExportRows(resources);
  if (rows.length === 0) {
    return 0;
  }
  downloadCsv(toCsv(EXPORT_COLUMN_HEADERS, rows), exportFileName(monthDate));
  return rows.length;
}
